import React from "react";

import "../../pages/reports/gce-o.css";
import { ExamType } from "../../types/types";

interface SubjectData {
  code: string;
  name: string;
  student_count: string | number;
}

interface CAPSubjectsTableProps {
  registrationData: {
    exam_registration?: {
      subject_categories?: Record<string, SubjectData[]>;
    } | null;
  } | null;
  selectedExamTypes: ExamType[];
}

// Helper to safely get and format subject data for a cell
const getSubjectName = (subject: SubjectData | undefined): string =>
  subject?.code && subject?.name ? `${subject.code} ${subject.name}` : "-";

// Helper to safely get student count for a cell
const getStudentCount = (subject: SubjectData | undefined): string | number =>
  subject?.student_count || "-";

// Helper to safely get and sort subject array
const getSortedSubjects = (
  categories: Record<string, SubjectData[]> | undefined,
  key: string
): SubjectData[] =>
  (categories?.[key] || []).sort((a, b) => a.code.localeCompare(b.code));

const CAPSubjectsTable: React.FC<CAPSubjectsTableProps> = ({
  registrationData,
  selectedExamTypes,
}) => {
  const shouldRenderTables = selectedExamTypes.some((et) =>
    et.name.includes("CAP")
  ); // Use .some() instead of finding the exact one and checking

  if (!shouldRenderTables) return null;

  const subjectCategories =
    registrationData?.exam_registration?.subject_categories;
  if (!subjectCategories) return null;

  // Use the helper to initialize and sort
  const capTst = getSortedSubjects(subjectCategories, "CAPTST");
  const capIt = getSortedSubjects(subjectCategories, "CAPIT");
  const capTstSpecialty = getSortedSubjects(
    subjectCategories,
    "CAPTSTSPECIALTY"
  );
  const capItSpecialty = getSortedSubjects(subjectCategories, "CAPITSPECIALTY");

  const maxBodyRows = 25; // Based on the original number of <tr> elements in the body

  const bodyRows = Array.from({ length: maxBodyRows }, (_, i) => {
    // Subject in first two columns: TST for i=0 to 11, then IT for i=12 to 24
    const subject = i < 12 ? capTst[i] : capIt[i - 12];
    const isTstRow = i < 12;

    // Determine the Specialty subject to display
    let specialtySubject: SubjectData | undefined;
    if (i < 4) {
      // Rows 1-4 (i=0 to 3) are TST Specialty
      specialtySubject = capTstSpecialty[i];
    } else if (i >= 4 && i < 6) {
      // Rows 5-6 (i=4 to 5) are TST Specialty but with IT headers
      specialtySubject = capTstSpecialty[i];
    } else if (i >= 6 && i < 17) {
      // Rows 7-17 (i=6 to 16) are IT Specialty
      specialtySubject = capItSpecialty[i - 6];
    }

    // Determine the specialty header/data cells
    let specialtyCells;
    if (i === 4) {
      // Row 5: IT Header
      specialtyCells = <th colSpan={2}>IT</th>;
    } else if (i === 5) {
      // Row 6: IT Subheader
      specialtyCells = (
        <>
          <td className="font-semibold">SPECIALTY</td>
          <td className="italic"># of candidates</td>
        </>
      );
    } else if (i >= 0 && i < 6) {
      // Rows 1-6 (TST Specialty data)
      specialtyCells = (
        <>
          <td>{specialtySubject?.name || "-"}</td>
          <td>{getStudentCount(specialtySubject)}</td>
        </>
      );
    } else if (i >= 6 && i < 17) {
      // Rows 7-17 (IT Specialty data)
      specialtyCells = (
        <>
          <td>{specialtySubject?.name || "-"}</td>
          <td>{getStudentCount(specialtySubject)}</td>
        </>
      );
    } else {
      // Remaining rows (i >= 17) have empty specialty columns
      specialtyCells = (
        <>
          <td>N/A</td>
          <td>N/A</td>
        </>
      );
    }

    return (
      <tr key={i}>
        <td colSpan={2}>{getSubjectName(subject)}</td>
        {/* CAP TST Count */}
        <td>{isTstRow ? getStudentCount(subject) : "N/A"}</td>
        {/* CAP IT Count */}
        <td>{!isTstRow ? getStudentCount(subject) : "N/A"}</td>
        {specialtyCells}
      </tr>
    );
  });

  // The header section remains the same as it is a fixed structure
  const headerRows = (
    <>
      <tr>
        <th colSpan={2} rowSpan={2}>
          Other Subjects
        </th>
        <th>CAP TST</th>
        <th>CAP IT</th>
        <th colSpan={2}>TST</th>
      </tr>
      <tr>
        <td className="italic"># of candidates</td>
        <td className="italic"># of candidates</td>
        <td className="font-semibold">SPECIALTY</td>
        <td className="italic"># of candidates</td>
      </tr>
    </>
  );

  return (
    <table className="gceo-subjects-table">
      <tbody>
        {headerRows}
        {bodyRows}
      </tbody>
    </table>
  );
};

export default CAPSubjectsTable;
